import { useEffect, lazy, Suspense } from 'react'
import { useDispatch, useSelector, useTranslation } from '@spcc/core'
import { Skeleton } from '@spcc/uikit'
import { salesDashboardActions } from '../../store/sales-dashboard.slice'
import { selectLastRefreshed, selectIsLoading } from '../../store/sales-dashboard.selectors'
import { SalesFiltersBar } from './SalesFiltersBar'
import { SalesStatsCards } from './SalesStatsCards'

const SalesDailyChart = lazy(() => import('./SalesDailyChart').then(m => ({ default: m.SalesDailyChart })))
const SalesComparisonChart = lazy(() => import('./SalesComparisonChart').then(m => ({ default: m.SalesComparisonChart })))
const SalesBreakdownTable = lazy(() => import('./SalesBreakdownTable').then(m => ({ default: m.SalesBreakdownTable })))
const TargetProgressSection = lazy(() => import('./TargetProgressSection').then(m => ({ default: m.TargetProgressSection })))

function formatLastUpdated(isoString: string | null): string {
  if (!isoString) return ''
  try {
    const date = new Date(isoString)
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export function SalesDashboardPage() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const lastRefreshed = useSelector(selectLastRefreshed)
  const isLoading = useSelector(selectIsLoading)

  useEffect(() => {
    dispatch(salesDashboardActions.initDashboard())
  }, [dispatch])

  const lastUpdatedText = formatLastUpdated(lastRefreshed)

  return (
    <div className="space-y-4 p-0 sm:space-y-6 sm:p-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {t('salesDashboard.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('salesDashboard.subtitle')}
          </p>
        </div>
        {lastUpdatedText && (
          <p className="text-xs text-muted-foreground">
            {t('salesDashboard.lastUpdated', { time: lastUpdatedText })}
            {isLoading && ' ...'}
          </p>
        )}
      </div>

      <SalesFiltersBar />
      <SalesStatsCards />

      <Suspense fallback={<Skeleton className="h-80 w-full" />}>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <SalesDailyChart />
          <SalesComparisonChart />
        </div>
      </Suspense>

      <Suspense fallback={<Skeleton className="h-64 w-full" />}>
        <SalesBreakdownTable />
      </Suspense>

      <Suspense fallback={<Skeleton className="h-48 w-full" />}>
        <TargetProgressSection />
      </Suspense>
    </div>
  )
}
